import { Card, Box, Typography, Button } from '@mui/material';
import { useState } from 'react';
import { MultiButtonModal } from 'shared/modals/MultiButtonModal/multiButtonModal';
import { zIndexValues } from 'utils/styles';
import logger from 'utils/logger';
import api from 'utils/api';
import { DOLPHIN_API } from 'shared/constants/apis';

const log = logger.createLogger('IWGDashboardFillData');

// THIS API is used to link the users to the organizations and employments
// This is bad design as each user should have an organization and employment when created
const IWGDashboardFillData = () => {
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleFillData = async () => {
    await api.post(DOLPHIN_API.ADMIN_FILL_DATA, {});
  };

  const handleFillEmploymentUserType = async () => {
    try {
      setIsLoading(true);
      const response = await api.post(
        DOLPHIN_API.ADMIN_FILL_EMPLOYMENT_USER_TYPE,
        {}
      );
      // Store the response data and open the modal
      setResultsData(response);
      setIsResultsModalOpen(true);
    } catch (error) {
      log.error('Error filling employment user type:', error);
      // Show error in modal
      setResultsData({
        message: 'Error occurred while processing the request',
        error: error.message || 'Unknown error',
        total: 0,
        updated: 0,
      });
      setIsResultsModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const closeResultsModal = () => {
    setIsResultsModalOpen(false);
    setResultsData(null);
  };

  const renderResultsModalContent = () => {
    if (!resultsData) return null;

    const { message, total, updated, errors, error } = resultsData;
    const hasErrors = errors && errors.length > 0;
    const isSuccess = !error && updated > 0;

    return (
      <Box>
        {/* Success/Error indicator */}
        <Box
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 1,
            backgroundColor: error
              ? '#ffebee'
              : isSuccess
              ? '#e8f5e9'
              : '#fff3e0',
            border: error
              ? '1px solid #f44336'
              : isSuccess
              ? '1px solid #4caf50'
              : '1px solid #ff9800',
          }}>
          <Typography
            variant="h6"
            sx={{
              color: error ? '#d32f2f' : isSuccess ? '#2e7d32' : '#f57c00',
              fontWeight: 'bold',
              mb: 1,
            }}>
            {error ? 'Error' : isSuccess ? 'Success' : 'Completed'}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {message}
          </Typography>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Box>

        {/* Statistics */}
        {!error && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
              Results Summary:
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body1">
                <strong>Total records processed:</strong> {total || 0}
              </Typography>
              <Typography variant="body1">
                <strong>Records updated:</strong> {updated || 0}
              </Typography>
              {hasErrors && (
                <Typography variant="body1" color="error">
                  <strong>Errors encountered:</strong> {errors.length}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Error details */}
        {hasErrors && (
          <Box>
            <Typography
              variant="h6"
              sx={{ mb: 1, fontWeight: 'bold', color: '#d32f2f' }}>
              Error Details:
            </Typography>
            <Box
              sx={{
                maxHeight: '200px',
                overflowY: 'auto',
                backgroundColor: '#fafafa',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 1,
              }}>
              {errors.map((err, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 1,
                    pb: 1,
                    borderBottom:
                      index < errors.length - 1 ? '1px solid #e0e0e0' : 'none',
                  }}>
                  <Typography variant="body2" color="error">
                    <strong>Employment ID {err.employmentId}:</strong>{' '}
                    {err.error}
                  </Typography>
                  {err.username && (
                    <Typography variant="caption" color="text.secondary">
                      User: {err.username} (ID: {err.userId})
                    </Typography>
                  )}
                  {err.userType && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block' }}>
                      User Type: {err.userType}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Card>
      <Box py={3} px={3} mt={3} mx={3}>
        <Box pl={1} mb={5} display="flex">
          <Typography variant="h3">{'Fill Data'}</Typography>
        </Box>
        <Box mb={3}>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}>
            This button operates on existing database entities
            &ldquo;Organization&rdquo; and &ldquo;QuotaOrg&rdquo;. It retrieves
            all records from these tables and performs the following operations:
          </Typography>
          <Box component="ul" sx={{ mt: 2, pl: 3 }}>
            <Typography
              component="li"
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1 }}>
              <strong>Employment Records:</strong> Goes through all
              organizations and their associated users (those affiliated or
              employed by the organization) and creates employment records where
              they don&rsquo;t already exist.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              <strong>Contract Records:</strong> Reviews all QuotaOrg rows and
              creates contract database records for source organization and
              destination organization pairs where contracts don&rsquo;t already
              exist.
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 2 }}>
            <strong>Note:</strong>
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            This operation should only be run once for historical data
            migration. Employment and Contract tables were added later, so
            existing historical data may not have these records.
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            This button fills in missing employment and contract records for
            legacy data. Or, it can be used as a final safety net to fill in
            missing data.
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" justifyContent="center">
          <Box display="flex" justifyContent="space-around" alignItems="center">
            <Button
              color="primary"
              variant="contained"
              sx={{ color: '#FFF' }}
              onClick={handleFillData}>
              Fill Data
            </Button>
          </Box>
        </Box>
      </Box>

      <Box py={3} px={3} mt={3} mx={3}>
        <Box pl={1} mb={5} display="flex">
          <Typography variant="h3">
            {'Fill "userType" column in Employment Table'}
          </Typography>
        </Box>
        <Box mb={3}>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}>
            This operation checks all employment records whose
            &ldquo;userType&rdquo; column is empty and fills in the value found
            in the corresponding User table&apos;s &ldquo;userType&rdquo;
            column.
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" justifyContent="center">
          <Box display="flex" justifyContent="space-around" alignItems="center">
            <Button
              color="primary"
              variant="contained"
              sx={{ color: '#FFF' }}
              onClick={handleFillEmploymentUserType}
              disabled={isLoading}>
              {isLoading
                ? 'Processing...'
                : "Fill Employment Table's userType column"}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Results Modal */}
      <MultiButtonModal
        isOpened={isResultsModalOpen}
        onClose={closeResultsModal}
        title="Fill Employment UserType Results"
        zIndex={zIndexValues.modal4}
        actionButtons={[
          {
            label: 'Close',
            variant: 'info',
            close: true,
            dataTestId: 'close-results-modal',
          },
        ]}>
        {renderResultsModalContent()}
      </MultiButtonModal>
    </Card>
  );
};

export default IWGDashboardFillData;
